/**
 * How people use the app, recorded in product_events next to the events the
 * database already writes on its own (analysis_started/completed/failed,
 * project_created, credits_spent, subscription_*, share_created...).
 *
 * Client events carry props.client = true. They are first-party product
 * telemetry for signed-in users, written through track_product_event() (auth
 * uid, snake_case names, 4 KB props, 120/min cap), and read in the admin.
 *
 * A handful are also marketing milestones and go to GA4 (consent-governed by
 * GTM): analysis_submitted, report_exported, report_shared, upgrade_clicked.
 */
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { shouldTrack } from "@/lib/analytics-tracker";
import { pushEvent } from "@/lib/gtm";

export type ProductEvent =
  // Navigation
  | "screen_viewed" // one per app route change, route template in props.screen
  // Creating an analysis
  | "analysis_type_selected"
  | "wizard_step_viewed"
  | "wizard_step_completed"
  | "analysis_submitted"
  // Using a report
  | "report_viewed"
  | "report_section_viewed"
  | "report_pdf_downloaded"
  | "report_share_link_created"
  | "report_link_copied"
  | "report_embed_copied"
  | "report_rerun_clicked"
  | "report_rated" // the rating card: props.score 1-10, props.refunded
  // Monetisation
  | "upgrade_prompt_shown"
  | "upgrade_prompt_clicked"
  | "feature_gated"
  | "checkout_viewed"
  // Account & growth
  | "client_created"
  | "referral_link_copied"
  | "referral_share"
  // Anything tagged in the UI with data-track (props.id holds the tag)
  | "ui_action";

type Subject = { type: string; id: string };

const DEDUPE_MS = 800;
const lastSent = new Map<string, number>();

/*
 * Set by TrackingManager from the auth state. Reports also render on public
 * share pages; a signed-out viewer has nothing to attribute the event to, so
 * nothing is sent (the RPC would refuse it anyway).
 */
let signedInUserId: string | null = null;
export function setProductAnalyticsUser(id: string | null): void {
  signedInUserId = id;
}

export function trackProduct(event: ProductEvent, props: Record<string, unknown> = {}, subject?: Subject): void {
  if (typeof window === "undefined" || !signedInUserId || !shouldTrack()) return;

  // The same event with the same props twice within a moment is a double
  // render or a double click, not two uses.
  const key = `${event}|${subject?.id ?? ""}|${JSON.stringify(props)}`;
  const now = Date.now();
  if (now - (lastSent.get(key) ?? 0) < DEDUPE_MS) return;
  lastSent.set(key, now);

  void supabase
    .rpc("track_product_event" as never, {
      p_event: event,
      p_props: props,
      p_subject_type: subject?.type ?? null,
      p_subject_id: subject?.id ?? null,
    } as never)
    .then(() => undefined, () => undefined); // builders only run when awaited; failures are not the user's problem

  mirrorToGa(event, props);
}

function mirrorToGa(event: ProductEvent, props: Record<string, unknown>): void {
  switch (event) {
    case "analysis_submitted":
      pushEvent("analysis_submitted", { analysis_type: props.analysis_type, is_first: props.is_first });
      break;
    case "report_pdf_downloaded":
      pushEvent("report_exported", { analysis_type: props.analysis_type, export_format: props.format ?? "pdf" });
      break;
    case "report_share_link_created":
    case "report_link_copied":
    case "report_embed_copied":
      pushEvent("report_shared", {
        analysis_type: props.analysis_type,
        share_method: event === "report_embed_copied" ? "embed" : event === "report_share_link_created" ? "link_created" : "link_copied",
      });
      break;
    case "upgrade_prompt_clicked":
      pushEvent("upgrade_clicked", { trigger: props.trigger });
      break;
    default:
      break;
  }
}

/** "/project/5f1c…/report" → "/project/:id/report": group screens, not records. */
export function screenTemplate(pathname: string): string {
  return (
    pathname
      .replace(/^\/(en|el)(?=\/|$)/, "")
      .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi, "/:id")
      .replace(/\/\d+(?=\/|$)/g, "/:n")
      .replace(/\/[A-Za-z0-9_-]{16,}(?=\/|$)/g, "/:token") || "/"
  );
}

/**
 * report_viewed once when a report mounts, then report_section_viewed on
 * every section change. One line in each report component.
 */
export function useReportTracking(report: string, section: string): void {
  const mounted = useRef(false);
  useEffect(() => {
    const screen = screenTemplate(window.location.pathname);
    if (!mounted.current) {
      mounted.current = true;
      trackProduct("report_viewed", { report, screen });
      return;
    }
    trackProduct("report_section_viewed", { report, section, screen });
  }, [report, section]);
}
